import { initialsFromName } from '../components/account/Avatar';

/**
 * Ready-made avatars, drawn in the brand palette so picking one never looks
 * like a placeholder.
 *
 * They are rendered to JPEG on the client and then travel the same upload path
 * as a photo, so the server keeps a single contract: a raster image it can
 * sniff. Nothing here needs a network request or an image asset.
 */

const SIZE = 512;

const INK = '#14141a';
const INK_DEEP = '#0e0e10';
const GOLD = '#c6a75e';
const GOLD_LIGHT = '#e3c983';
const SILK = '#e6e6e8';
const GRAPHITE = '#2a2a2e';

function fill(ctx, color) {
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, SIZE, SIZE);
}

function drawInitials(ctx, text, color, { weight = 600, scale = 0.38 } = {}) {
  ctx.fillStyle = color;
  ctx.font = `${weight} ${Math.round(SIZE * scale)}px Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  // Optical centre: cap-height glyphs sit slightly low on the em box.
  ctx.fillText(text, SIZE / 2, SIZE / 2 + SIZE * 0.015);
}

function ring(ctx, radius, width, color) {
  ctx.beginPath();
  ctx.arc(SIZE / 2, SIZE / 2, radius, 0, Math.PI * 2);
  ctx.lineWidth = width;
  ctx.strokeStyle = color;
  ctx.stroke();
}

/* The Velox V, as a single outline. Unit coordinates traced from the logo: the
   left arm is the heavy one and the right arm starts lower, which is what
   stops it reading as a generic chevron. */
const V_MARK = [
  [0, 0],
  [0.26, 0],
  [0.545, 0.735],
  [0.782, 0.19],
  [1, 0.19],
  [0.648, 1],
  [0.388, 1],
];

function drawVMark(ctx, color, { scale = 0.52 } = {}) {
  const w = SIZE * scale;
  const h = SIZE * scale * 1.02;
  const x = (SIZE - w) / 2;
  const y = (SIZE - h) / 2;

  ctx.beginPath();
  V_MARK.forEach(([ux, uy], i) => {
    const px = x + ux * w;
    const py = y + uy * h;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  });
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
}

/* Monograms carry the initials; marks are for people who would rather not
   show letters at all. */
const PRESETS = [
  {
    id: 'gold',
    label: 'Gold monogram',
    draw: (ctx, initials) => {
      const g = ctx.createLinearGradient(0, 0, SIZE, SIZE);
      g.addColorStop(0, GOLD_LIGHT);
      g.addColorStop(1, GOLD);
      fill(ctx, INK);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, SIZE, SIZE);
      drawInitials(ctx, initials, INK_DEEP);
    },
  },
  {
    id: 'ink',
    label: 'Gold on ink',
    draw: (ctx, initials) => {
      fill(ctx, INK);
      ring(ctx, SIZE * 0.42, SIZE * 0.012, 'rgba(198, 167, 94, 0.55)');
      drawInitials(ctx, initials, GOLD);
    },
  },
  {
    id: 'silk',
    label: 'Silk monogram',
    draw: (ctx, initials) => {
      fill(ctx, SILK);
      drawInitials(ctx, initials, INK_DEEP);
    },
  },
  {
    id: 'graphite',
    label: 'Graphite monogram',
    draw: (ctx, initials) => {
      const g = ctx.createLinearGradient(0, 0, 0, SIZE);
      g.addColorStop(0, GRAPHITE);
      g.addColorStop(1, INK_DEEP);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, SIZE, SIZE);
      drawInitials(ctx, initials, SILK, { weight: 500 });
    },
  },
  {
    id: 'mark-gold',
    label: 'Velox mark, gold',
    draw: (ctx) => {
      fill(ctx, INK);
      drawVMark(ctx, GOLD);
    },
  },
  {
    id: 'mark-ink',
    label: 'Velox mark, ink',
    draw: (ctx) => {
      const g = ctx.createLinearGradient(0, 0, SIZE, SIZE);
      g.addColorStop(0, GOLD_LIGHT);
      g.addColorStop(1, GOLD);
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, SIZE, SIZE);
      drawVMark(ctx, INK_DEEP);
    },
  },
  {
    id: 'rings',
    label: 'Rings',
    draw: (ctx) => {
      fill(ctx, INK);
      ring(ctx, SIZE * 0.3, SIZE * 0.022, GOLD);
      ring(ctx, SIZE * 0.4, SIZE * 0.008, 'rgba(198, 167, 94, 0.45)');
    },
  },
  {
    id: 'halo',
    label: 'Halo',
    draw: (ctx) => {
      fill(ctx, INK_DEEP);
      const g = ctx.createRadialGradient(SIZE * 0.5, SIZE * 0.42, 0, SIZE * 0.5, SIZE * 0.42, SIZE * 0.5);
      g.addColorStop(0, 'rgba(198, 167, 94, 0.85)');
      g.addColorStop(0.55, 'rgba(198, 167, 94, 0.18)');
      g.addColorStop(1, 'rgba(198, 167, 94, 0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, SIZE, SIZE);
    },
  },
];

/**
 * Render every preset for a given name. Returns
 * `[{ id, label, dataUrl }]` — the same data URL is used for the swatch shown
 * in the picker and for the upload, so what you tap is exactly what you get.
 */
export function buildPresetAvatars(name) {
  const initials = initialsFromName(name);

  return PRESETS.map(({ id, label, draw }) => {
    const canvas = document.createElement('canvas');
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    draw(ctx, initials);
    return { id, label, dataUrl: canvas.toDataURL('image/jpeg', 0.9) };
  }).filter(Boolean);
}

export default buildPresetAvatars;
